import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowDownOutlined, DollarOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCreateLayoutMutation } from '../../../store/api/calculationsApi';
import type {
  Application,
  ApplicationCargo,
  ApplicationClientFinancialObligation,
  ApplicationCondition,
  RouteApplication,
} from '../../../types';
import type { ApplicationControlTaskRow, WorkspaceTask } from '../../../store/api/workspaceApi';
import { useGetApplicationControlTasksQuery, workspaceApi } from '../../../store/api/workspaceApi';
import { applicationRouteDirectionLabel, getRouteDirection } from '../../../utils/addressUtils';
import {
  clientPlannedObligations,
  mapApplicationIncomeObligationToBlockItem,
  pickClientObligation,
  resolveApplicationClientContractSummary,
  sumPaidClientObligation,
} from '../../../utils/applicationClientSettlementProjection';
import { formatExternalApplicationNumber } from '../../../utils/applicationDisplayFormat';
import { formatResponsibleUserLabel } from '../../../utils/userDisplayName';
import { APPLICATION_CONDITION_LABELS, formatApplicationCondition } from '../../../utils/applicationConditions';
import { navigateToReferenceCard } from '../../../utils/referenceCardNavigation';
import ApplicationEventsModal from '../ApplicationEventsModal';
import ApplicationTasksTab from '../ApplicationTasksTab';
import ApplicationDocumentsTab from '../ApplicationDocumentsTab';
import CargoDetailsModal from '../../cargo/CargoDetailsModal';
import { CreateClientInvoiceModal } from '../../finance';
import CreatePaymentModal from '../../finance/CreatePaymentModal';
import type { RouteObligationForPayment } from '../../../store/api/financeApi';
import { ObligationPaymentsBlock } from '../../../pages/finance/FinanceRouteModal';
import { ApplicationCell, DataTable, ExternalApplicationCell, MoneyCell, StatusCell } from '../../../ui';
import { cargoApi, useGetCargoByIdQuery } from '../../../store/api/cargoApi';
import { useGetRoutesByApplicationQuery } from '../../../store/api/routesApi';
import type { AppDispatch } from '../../../store';
import { fetchCargoAllocationIndexForApplications } from '../../../utils/fetchCargoAllocationIndexForApplications';
import { isCargoLifecycleProjectionUiEnabled } from '../../../services/cargoLifecycle/cargoLifecycleProjectionFlags';
import { useCargoLifecycleProjectionIndex } from '../../../hooks/useCargoLifecycleProjectionIndex';
import { resolveCargoCounterSnapshot } from '../../../utils/cargoCounterSnapshot';
import type { CargoCounterSnapshot } from '../../../services/cargoLifecycle/cargoLifecycleProjectionTypes';
import {
  cargoHistoryDescriptionDisplayText,
  cargoHistoryEventDisplayText,
  cargoHistoryRowIsStale,
  mergeCargoHistories,
  type CargoHistoryRowWithName,
} from '../../../utils/applicationCargoHistoryDisplay';
import { transportStatusTagColor } from '../../../utils/cargoDerivedTransportStatus';
import { originalsFinanceStageMeta } from './originalsFinanceStatus';
import {
  CLIENT_PAYMENT_TIMELINE_META,
  obligationStepStatus,
} from './clientPaymentStepStatus';
import './ApplicationDetailsPanel.css';

const { Text } = Typography;

const sectionCardStyle: React.CSSProperties = { height: '100%' };
function formatApplicationPrepaymentConditionLine(condition: string | null | undefined): string {
  const v = String(condition || '').trim().toUpperCase();
  if (!v) return '—';
  const map: Record<string, string> = {
    BEFORE_LOADING: 'До погрузки',
    AFTER_LOADING: 'После погрузки',
    BEFORE_UNLOADING: 'До выгрузки',
    AFTER_UNLOADING: 'После выгрузки',
    INVOICE_RECEIVED: 'После получения счёта',
    ON_LOADING: 'При погрузке',
    OTHER: 'Иное',
  };
  return map[v] || String(condition).trim() || '—';
}

function mapApplicationControlTasksToWorkspaceTasks(
  rows: ApplicationControlTaskRow[],
  application: Application
): WorkspaceTask[] {
  const applicationDirection = applicationRouteDirectionLabel({
    loadingCity: (application as any).loadingCity,
    unloadingCity: (application as any).unloadingCity,
    loadingAddress: application.loadingAddress,
    unloadingAddress: application.unloadingAddress,
  });
  return rows.map((t) => ({
    id: t.id,
    taskType: t.taskType,
    status: t.status,
    dueDate: t.dueDate ?? null,
    routeId: null,
    applicationId: application.id,
    entityType: 'APPLICATION',
    invoiceId: t.invoiceId ?? null,
    obligationId: t.obligationId ?? null,
    obligationStage: t.obligationStage ?? null,
    routeStatus: null,
    loadingCity: null,
    unloadingCity: null,
    carrierName: null,
    carrierAgreement: null,
    applicationStatus: application.status,
    applicationNumber: application.internalNumber,
    applicationExternalNumber: application.externalNumber,
    applicationDate: application.applicationDate,
    applicationCreatedAt: application.createdAt,
    applicationDirection,
    applicationClientName: application.client?.name?.trim() || null,
  }));
}

function isApplicationInvoiceSentForFinance(
  prepObl: ApplicationClientFinancialObligation | undefined
): boolean {
  return prepObl?.baseDate != null && String(prepObl.baseDate).trim() !== '';
}

const sectionBodyStyle = { body: { padding: '12px 14px' } };

function formatPaymentTypeLine(app: Application): string {
  const raw = String(app.paymentMethod || '').trim().toLowerCase();
  if (raw === 'cash' || raw === 'fuel') return 'Нал';
  if (raw === 'non_cash_vat') return 'Безнал с НДС';
  if (raw === 'non_cash_no_vat') return 'Безнал без НДС';
  const u = raw.toUpperCase();
  if (u === 'CASH' || u === 'FUEL') return 'Нал';
  if (u === 'NON_CASH_VAT') return 'Безнал с НДС';
  if (u === 'NON_CASH_NO_VAT') return 'Безнал без НДС';
  return '—';
}

function formatDateShort(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = dayjs(date);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
}

function moneyOrDash(value: unknown, type: 'income' | 'expense' = 'income'): React.ReactNode {
  if (value === null || value === undefined || value === '') return <Text type="secondary">—</Text>;
  const n = Number(value);
  if (!Number.isFinite(n)) return <Text type="secondary">—</Text>;
  return <MoneyCell value={n} type={type} />;
}

function transportTypeLabel(value: string | null | undefined): string {
  const v = String(value || '').toLowerCase();
  if (v === 'auto') return 'Авто';
  if (v === 'railway') return 'ЖД';
  if (v === 'aviation') return 'Авиа';
  return value || '—';
}

const InfoSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card size="small" title={title} style={sectionCardStyle} styles={sectionBodyStyle}>
    <Descriptions column={1} size="small">
      {children}
    </Descriptions>
  </Card>
);

export function formatApplicationPageTitle(application: Application): string {
  const ext = formatExternalApplicationNumber(application);
  const internal = String(application.internalNumber || '').trim() || String(application.id);
  return ext !== '—' ? `Заявка ${internal} (${ext})` : `Заявка ${internal}`;
}

/** Карточка контрагента поверх текущей страницы (как OperationBlock / маршруты). */
function EntityReferenceLink(props: {
  to: string;
  children: React.ReactNode;
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
}) {
  const { to, children, navigate, location } = props;
  return (
    <Button
      type="link"
      className="routes-entity-link"
      style={{ padding: 0, height: 'auto', cursor: 'pointer' }}
      onClick={() => navigateToReferenceCard(navigate, to, location)}
    >
      {children}
    </Button>
  );
}

interface ApplicationDetailsProps {
  application: Application;
  focusSection?: 'finance' | null;
  initialTabKey?: string;
  openCreateInvoiceOnShow?: boolean;
  onCreateInvoiceIntentHandled?: () => void;
  onRefresh?: () => void | Promise<void>;
}

const ApplicationDetails: React.FC<ApplicationDetailsProps> = ({
  application,
  focusSection,
  initialTabKey,
  openCreateInvoiceOnShow = false,
  onCreateInvoiceIntentHandled,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [createLayout] = useCreateLayoutMutation();
  const { data: applicationControlTaskRows = [], refetch: refetchApplicationControlTasks } =
    useGetApplicationControlTasksQuery(application.id, { skip: !application.id });
  const [activeTab, setActiveTab] = useState(() => {
    const allowed = new Set(['main', 'tasks', 'finance', 'cargo', 'routes', 'penalties']);
    const k = initialTabKey && allowed.has(initialTabKey) ? initialTabKey : 'main';
    return k;
  });

  useEffect(() => {
    const allowed = new Set(['main', 'tasks', 'finance', 'cargo', 'routes', 'penalties']);
    const k = initialTabKey && allowed.has(initialTabKey) ? initialTabKey : 'main';
    setActiveTab(k);
  }, [application.id, initialTabKey]);
  const [isEventsModalVisible, setIsEventsModalVisible] = useState(false);
  const [isCreateInvoiceModalVisible, setIsCreateInvoiceModalVisible] = useState(false);
  const [clientPayModalVisible, setClientPayModalVisible] = useState(false);
  const [clientPayObligationId, setClientPayObligationId] = useState<number | null>(null);
  const [openCargoId, setOpenCargoId] = useState<number | null>(null);
  const [allocationIndex, setAllocationIndex] = useState(
    () => new Map<number, { inRoutesTotal: number; byRoute: { routeId: number; amount: number }[] }>()
  );
  const [allocationIndexLoading, setAllocationIndexLoading] = useState(false);
  const projectionUiEnabled = isCargoLifecycleProjectionUiEnabled();
  const [cargoTabHistoryRows, setCargoTabHistoryRows] = useState<CargoHistoryRowWithName[]>([]);
  const [cargoTabHistoryLoading, setCargoTabHistoryLoading] = useState(false);

  const cargoHistoryFetchKey = useMemo(
    () =>
      (application.cargo ?? [])
        .map((c) => `${c.id}:${String(c.name ?? '')}`)
        .sort()
        .join('|'),
    [application.cargo]
  );

  const cargoHistorySnapshotRef = useRef<{
    key: string;
    items: Array<{ id: number; name: string }>;
  }>({ key: '', items: [] });

  if (cargoHistoryFetchKey !== cargoHistorySnapshotRef.current.key) {
    cargoHistorySnapshotRef.current = {
      key: cargoHistoryFetchKey,
      items: (application.cargo ?? []).map((c) => ({
        id: c.id,
        name: c.name?.trim() || '—',
      })),
    };
  }

  const applicationCargoIds = useMemo(
    () =>
      (application.cargo ?? [])
        .map((c) => Number(c.id))
        .filter((n) => Number.isFinite(n) && n > 0),
    [application.cargo]
  );

  const clientIncomeObligationsForModal = useMemo((): RouteObligationForPayment[] => {
    const rows = application.financialObligations ?? [];
    return rows
      .filter(
        (o) =>
          String(o.direction || '').toUpperCase() === 'INCOME' &&
          String(o.counterpartyType || 'CLIENT').toUpperCase() === 'CLIENT' &&
          !(o as { isAdjustment?: boolean }).isAdjustment &&
          o.is_active !== false
      )
      .map((o) => {
        const paid = sumPaidClientObligation(o);
        const planned = Number(o.amountWithVAT ?? 0);
        const rem = Math.max(0, planned - paid);
        if (rem <= 0.005) return null;
        return {
          id: o.id,
          direction: 'INCOME',
          stage: String(o.stage),
          amountWithVAT: planned,
          remainingAmount: rem,
          dueDate: o.dueDate ?? null,
          routeId: null,
          applicationId: application.id,
        } as RouteObligationForPayment;
      })
      .filter((x): x is RouteObligationForPayment => x != null);
  }, [application.financialObligations, application.id]);

  const {
    data: appRoutesForAlloc,
    isFetching: appRoutesForAllocFetching,
  } = useGetRoutesByApplicationQuery(application.id, { skip: !application.id });

  const routeIdsKey = useMemo(() => {
    const list = Array.isArray(appRoutesForAlloc) ? appRoutesForAlloc : [];
    return list
      .map((r: { id?: number }) => Number(r?.id))
      .filter((n: number) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b)
      .join(',');
  }, [appRoutesForAlloc]);

  const routeStatusById = useMemo(() => {
    const m = new Map<number, string>();
    const list = Array.isArray(appRoutesForAlloc) ? appRoutesForAlloc : [];
    for (const r of list) {
      const id = Number((r as { id?: number })?.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      const st = (r as { status?: string }).status;
      if (st != null && String(st).trim() !== '') m.set(id, String(st));
    }
    return m;
  }, [appRoutesForAlloc]);

  const routeStatusesFingerprint = useMemo(
    () =>
      Array.from(routeStatusById.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([id, st]) => `${id}:${st}`)
        .join(','),
    [routeStatusById]
  );

  const {
    index: projectionIndex,
    loading: projectionIndexLoading,
    failed: projectionIndexFailed,
  } = useCargoLifecycleProjectionIndex(applicationCargoIds, {
    enabled: projectionUiEnabled && !!application.id,
    refreshKey: `${application.id}|${cargoHistoryFetchKey}|${routeStatusesFingerprint}`,
  });

  const resolveCounters = (record: ApplicationCargo): CargoCounterSnapshot =>
    resolveCargoCounterSnapshot({
      cargoId: record.id,
      quantity: Number(record.quantity ?? 0),
      projectionIndex,
      legacyIndex: allocationIndex,
      routeStatusById,
      useProjection: projectionUiEnabled,
    });

  const countersLoading = projectionUiEnabled ? projectionIndexLoading : allocationIndexLoading;

  const projectionIndexFingerprint = useMemo(() => {
    if (!projectionIndex.size) return '';
    return Array.from(projectionIndex.entries())
      .sort((a, b) => a[0] - b[0])
      .map(
        ([cid, p]) =>
          `${cid}:${p.inRoutesActive}:${p.inTransitActive}:${p.deliveredTotal}:${p.byRoute
            .map((x) => `${x.routeId}:${x.active}`)
            .sort()
            .join(',')}`
      )
      .join('|');
  }, [projectionIndex]);

  const applicationRoutesLinkKey = useMemo(
    () =>
      (application.routes || [])
        .map((item) => Number(item.routeId ?? (item as { route?: { id?: number } }).route?.id))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)
        .join(','),
    [application.routes]
  );

  const allocationIndexFingerprint = useMemo(() => {
    if (!allocationIndex.size) return '';
    return Array.from(allocationIndex.entries())
      .sort((a, b) => a[0] - b[0])
      .map(
        ([cid, v]) =>
          `${cid}:${v.inRoutesTotal}:${v.byRoute.map((x) => `${x.routeId}:${x.amount}`).sort().join(',')}`
      )
      .join('|');
  }, [allocationIndex]);

  useEffect(() => {
    if (projectionUiEnabled) {
      setAllocationIndex(new Map());
      setAllocationIndexLoading(false);
      return;
    }
    if (!application.id) {
      setAllocationIndex(new Map());
      setAllocationIndexLoading(false);
      return;
    }
    if (appRoutesForAllocFetching) {
      setAllocationIndexLoading(true);
      return;
    }
    const list = Array.isArray(appRoutesForAlloc) ? appRoutesForAlloc : [];
    const routeIds = list
      .map((r: { id?: number }) => Number(r?.id))
      .filter((n: number) => Number.isFinite(n) && n > 0);
    if (routeIds.length === 0) {
      setAllocationIndex(new Map());
      setAllocationIndexLoading(false);
      return;
    }
    let cancelled = false;
    setAllocationIndexLoading(true);
    (async () => {
      try {
        const index = await fetchCargoAllocationIndexForApplications(dispatch, [application.id]);
        if (!cancelled) {
          setAllocationIndex(index);
        }
      } catch (e) {
        console.error('[ApplicationDetailsPanel] getAllocations failed', { applicationId: application.id, e });
        message.warning('Не удалось загрузить распределение груза по маршрутам');
      } finally {
        if (!cancelled) {
          setAllocationIndexLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    application.id,
    appRoutesForAlloc,
    appRoutesForAllocFetching,
    applicationRoutesLinkKey,
    dispatch,
    projectionUiEnabled,
  ]);

  useEffect(() => {
    const cargoHistoryItems = cargoHistorySnapshotRef.current.items;
    if (!application.id || cargoHistoryItems.length === 0) {
      setCargoTabHistoryRows([]);
      setCargoTabHistoryLoading(false);
      return;
    }
    let cancelled = false;
    setCargoTabHistoryLoading(true);
    (async () => {
      const results = await Promise.all(
        cargoHistoryItems.map(async (c) => {
          try {
            const res = await dispatch(
              cargoApi.endpoints.getCargoHistory.initiate({ cargoId: c.id }, { forceRefetch: true })
            ).unwrap();
            const entries = Array.isArray(res?.data) ? res.data : [];
            return { cargoId: c.id, cargoName: c.name, entries };
          } catch {
            return { cargoId: c.id, cargoName: c.name, entries: [] };
          }
        })
      );
      if (!cancelled) {
        setCargoTabHistoryRows(mergeCargoHistories(results));
        setCargoTabHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    application.id,
    cargoHistoryFetchKey,
    dispatch,
    routeIdsKey,
    allocationIndexFingerprint,
    projectionIndexFingerprint,
    projectionUiEnabled,
    applicationRoutesLinkKey,
    routeStatusesFingerprint,
  ]);

  const cargoModalHistoryKey = useMemo(
    () =>
      `${projectionUiEnabled ? projectionIndexFingerprint : allocationIndexFingerprint}|${routeStatusesFingerprint}`,
    [allocationIndexFingerprint, projectionIndexFingerprint, projectionUiEnabled, routeStatusesFingerprint]
  );

  const {
    data: cargoDetails,
    isFetching: cargoDetailsFetching,
    isLoading: cargoDetailsLoading,
    isError: cargoDetailsError,
  } = useGetCargoByIdQuery(openCargoId!, { skip: openCargoId === null });
  const cargoDetailsLoadingVisible =
    openCargoId !== null && !cargoDetails && (cargoDetailsLoading || cargoDetailsFetching);

  useEffect(() => {
    if (cargoDetailsError && openCargoId != null) {
      message.error('Не удалось загрузить груз');
      setOpenCargoId(null);
    }
  }, [cargoDetailsError, openCargoId]);

  const cargoModalCounterSnapshot = useMemo(() => {
    if (openCargoId == null || !cargoDetails?.id) return null;
    return resolveCargoCounterSnapshot({
      cargoId: cargoDetails.id,
      quantity: Number(cargoDetails.quantity ?? 0),
      projectionIndex,
      legacyIndex: allocationIndex,
      routeStatusById,
      useProjection: projectionUiEnabled,
    });
  }, [openCargoId, cargoDetails, allocationIndex, projectionIndex, routeStatusById, projectionUiEnabled]);

  useEffect(() => {
    if (focusSection === 'finance') {
      setActiveTab('finance');
    }
  }, [focusSection]);

  useEffect(() => {
    if (!openCreateInvoiceOnShow) return;
    setActiveTab('finance');
    setIsCreateInvoiceModalVisible(true);
    onCreateInvoiceIntentHandled?.();
  }, [application, onCreateInvoiceIntentHandled, openCreateInvoiceOnShow]);

  const applicationTasks = useMemo<WorkspaceTask[]>(
    () => mapApplicationControlTasksToWorkspaceTasks(applicationControlTaskRows, application),
    [applicationControlTaskRows, application]
  );

  const routeDirection = applicationRouteDirectionLabel({
    loadingCity: (application as any).loadingCity,
    unloadingCity: (application as any).unloadingCity,
    loadingAddress: application.loadingAddress,
    unloadingAddress: application.unloadingAddress,
  });

  const transportTypes = useMemo(() => {
    const values = new Set<string>();
    (application.routes || []).forEach((item) => {
      if (item.route?.transportType) values.add(transportTypeLabel(item.route.transportType));
    });
    return Array.from(values).join(', ') || '—';
  }, [application.routes]);

  const renderActions = () => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
      <Space wrap>
        <Button
          size="small"
          onClick={async () => {
            try {
              const draft: any = await createLayout({
                applicationId: application.id,
                inputPayload: { source: 'application' },
              }).unwrap();
              window.location.href = `/calculations/${draft.id}`;
            } catch (e) {
              /* empty */
            }
          }}
        >
          Расчёт размещения
        </Button>
        <Button size="small" icon={<HistoryOutlined />} onClick={() => setIsEventsModalVisible(true)}>
          История событий
        </Button>
        <Button size="small" icon={<DollarOutlined />} onClick={() => setIsCreateInvoiceModalVisible(true)}>
          Создать счёт
        </Button>
      </Space>
    </div>
  );

  const renderMainTab = () => (
    <Card size="small" title="Основная информация">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={12}>
            <InfoSection title="Общая информация">
              <Descriptions.Item label="№ заявки">
                <ApplicationCell application={application} forDescriptionCard />
              </Descriptions.Item>
              <Descriptions.Item label="Внешний №">
                <ExternalApplicationCell application={application} forDescriptionCard />
              </Descriptions.Item>
              <Descriptions.Item label="Статус">
                <StatusCell status={application.status} />
              </Descriptions.Item>
            </InfoSection>
          </Col>
          <Col xs={24} lg={12}>
            <InfoSection title="Участники">
              <Descriptions.Item label="Клиент">
                {application.client?.id ? (
                  <EntityReferenceLink
                    to={`/clients/${application.client.id}`}
                    navigate={navigate}
                    location={location}
                  >
                    {application.client.name?.trim() || '—'}
                  </EntityReferenceLink>
                ) : (
                  <Text>{application.client?.name?.trim() || '—'}</Text>
                )}
              </Descriptions.Item>
              {application.expeditor ? (
                <Descriptions.Item label="Экспедитор">
                  <EntityReferenceLink
                    to={`/contractors/${application.expeditor.id}`}
                    navigate={navigate}
                    location={location}
                  >
                    {application.expeditor.name?.trim() || '—'}
                  </EntityReferenceLink>
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Грузоотправитель">
                {application.shipper?.id ? (
                  <EntityReferenceLink
                    to={`/contractors/${application.shipper.id}`}
                    navigate={navigate}
                    location={location}
                  >
                    {application.shipper.name?.trim() || '—'}
                  </EntityReferenceLink>
                ) : (
                  <Text>{application.shipper?.name?.trim() || '—'}</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Грузополучатель">
                {application.consignee?.id ? (
                  <EntityReferenceLink
                    to={`/contractors/${application.consignee.id}`}
                    navigate={navigate}
                    location={location}
                  >
                    {application.consignee.name?.trim() || '—'}
                  </EntityReferenceLink>
                ) : (
                  <Text>{application.consignee?.name?.trim() || '—'}</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Логист">
                {formatResponsibleUserLabel(application.responsibleLogist)}
              </Descriptions.Item>
            </InfoSection>
          </Col>
          <Col xs={24} lg={12}>
            <InfoSection title="Маршрут">
              <Descriptions.Item label="Направление">
                <Text strong>{routeDirection}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Точка погрузки">{application.loadingAddress || '—'}</Descriptions.Item>
              <Descriptions.Item label="Точка выгрузки">{application.unloadingAddress || '—'}</Descriptions.Item>
              <Descriptions.Item label="Плановая погрузка">{formatDateShort(application.plannedLoadingDate)}</Descriptions.Item>
              <Descriptions.Item label="Плановая выгрузка">{formatDateShort(application.plannedUnloadingDate)}</Descriptions.Item>
            </InfoSection>
          </Col>
          <Col xs={24} lg={12}>
            <InfoSection title="Условия">
              <Descriptions.Item label="Тип перевозки">{transportTypes}</Descriptions.Item>
              <Descriptions.Item label="Основание">
                {application.contractId != null && application.contract
                  ? `Договор №${application.contract.number} от ${formatDateShort(application.contract.date)}`
                  : 'Договор-заявка клиента'}
              </Descriptions.Item>
              <Descriptions.Item label="Комментарий">
                {application.requiredConditions || <Text type="secondary">—</Text>}
              </Descriptions.Item>
            </InfoSection>
          </Col>
        </Row>
      </Space>
    </Card>
  );

  const renderTasksTab = () => (
    <ApplicationTasksTab
      application={application}
      tasks={applicationTasks}
      onTaskSuccess={async () => {
        dispatch(
          workspaceApi.util.invalidateTags([
            'Workspace',
            { type: 'ApplicationControlTasks', id: application.id },
            { type: 'ApplicationTaskHistory', id: application.id },
          ])
        );
        await refetchApplicationControlTasks();
        await onRefresh?.();
      }}
    />
  );

  const renderFinanceTab = () => {
    const rows = application.financialObligations;
    const clientPlannedRows = clientPlannedObligations(application);
    const orderedClientObl = [...clientPlannedRows].sort((a, b) => {
      const rk = (s: string) => (String(s).toUpperCase() === 'PREPAYMENT' ? 0 : 1);
      return rk(String(a.stage)) - rk(String(b.stage));
    });
    const prepObl = pickClientObligation(rows, 'PREPAYMENT');
    const finalObl = pickClientObligation(rows, 'FINAL');
    const { contractTotalRequired, totalPaidAll, balanceRemaining, status } =
      resolveApplicationClientContractSummary(application);
    const hasPrepayment = Number(application.prepaymentPercent ?? 0) > 0;
    const hasFinalPayment = Number(application.mainPaymentPercent ?? 0) > 0;
    const showClientIncomeStages = clientPlannedRows.length > 0;
    const invoiceSent = isApplicationInvoiceSentForFinance(prepObl);
    const originalsStage = originalsFinanceStageMeta(application.originalsReceivedAt);
    const prepStatus = obligationStepStatus(prepObl);
    const finalStatus = obligationStepStatus(finalObl);
    const finalPaymentTimelineLabel =
      showClientIncomeStages && prepObl && Number(prepObl.amountWithVAT ?? 0) > 0.005
        ? 'Доплата'
        : hasPrepayment
          ? 'Доплата'
          : 'Оплата';

    return (
      <Card size="small" title="Финансы заявки">
        <Space direction="vertical" size={18} style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 10 }}>
              Этапы оплаты
            </Text>
            <Space size={12} wrap align="start">
              <Space size={6} style={{ minWidth: 150 }}>
                <Text>Счёт</Text>
                <Tag color={invoiceSent ? 'green' : 'default'}>{invoiceSent ? 'Отправлен' : 'Не отправлен'}</Tag>
              </Space>
              {showClientIncomeStages ? (
                <Space size={6} style={{ minWidth: 150 }}>
                  <Text>Предоплата</Text>
                  <Tag color={CLIENT_PAYMENT_TIMELINE_META[prepStatus].color}>
                    {CLIENT_PAYMENT_TIMELINE_META[prepStatus].label}
                  </Tag>
                </Space>
              ) : null}
              {hasFinalPayment ? (
                <>
                  <Space size={6} style={{ minWidth: 150 }}>
                    <Text>Оригиналы ТН</Text>
                    <Tag color={originalsStage.color}>
                      {originalsStage.label}
                    </Tag>
                  </Space>
                  <Space size={6} style={{ minWidth: 150 }}>
                    <Text>{finalPaymentTimelineLabel}</Text>
                    <Tag color={CLIENT_PAYMENT_TIMELINE_META[finalStatus].color}>
                      {CLIENT_PAYMENT_TIMELINE_META[finalStatus].label}
                    </Tag>
                  </Space>
                </>
              ) : showClientIncomeStages ? (
                <Space size={6} style={{ minWidth: 150 }}>
                  <Text>{finalPaymentTimelineLabel}</Text>
                  <Tag color={CLIENT_PAYMENT_TIMELINE_META[finalStatus].color}>
                    {CLIENT_PAYMENT_TIMELINE_META[finalStatus].label}
                  </Tag>
                </Space>
              ) : null}
            </Space>
          </div>

          <Card
            size="small"
            title={
              <Space>
                <ArrowDownOutlined style={{ color: '#52c41a' }} />
                <span>Получить от клиента</span>
              </Space>
            }
            style={{ backgroundColor: '#f6ffed' }}
          >
            {orderedClientObl.length > 0 ? (
              orderedClientObl.map((obl) => (
                <ObligationPaymentsBlock
                  key={obl.id}
                  obligation={mapApplicationIncomeObligationToBlockItem(obl)}
                  visible={activeTab === 'finance'}
                  moneyType="income"
                  onAddPayment={() => {
                    setClientPayObligationId(obl.id);
                    setClientPayModalVisible(true);
                  }}
                />
              ))
            ) : (
              <Text type="secondary">
                Плановые обязательства (предоплата / доплата) появятся после сохранения ставки или суммы по договору.
              </Text>
            )}
          </Card>

          <Card size="small" title="Условия оплаты">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Ставка">{moneyOrDash(application.totalCostWithVAT)}</Descriptions.Item>
              <Descriptions.Item label="Тип оплаты">{formatPaymentTypeLine(application)}</Descriptions.Item>
              <Descriptions.Item label="Тип предоплаты">
                {formatApplicationPrepaymentConditionLine(application.prepaymentCondition)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 10 }}>
              Статус оплаты
            </Text>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card size="small">
                  <Text type="secondary">Статус</Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color={status.color}>{status.label}</Tag>
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card size="small">
                  <Text type="secondary">Оплачено / требуется</Text>
                  <div style={{ marginTop: 8 }}>
                    {moneyOrDash(totalPaidAll)} <Text type="secondary">/</Text>{' '}
                    {moneyOrDash(contractTotalRequired)}
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card size="small">
                  <Text type="secondary">Остаток</Text>
                  <div style={{ marginTop: 8 }}>{moneyOrDash(balanceRemaining)}</div>
                </Card>
              </Col>
            </Row>
          </div>
        </Space>
      </Card>
    );
  };

  const renderCargoTab = () => {
    const cargo = application.cargo || [];
    const totals = cargo.reduce(
      (acc, item) => ({
        places: acc.places + (Number(item.quantity) || 0),
        weight: acc.weight + (Number(item.totalWeightKg) || 0),
        volume: acc.volume + (Number(item.volumeM3) || 0),
      }),
      { places: 0, weight: 0, volume: 0 }
    );

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title="Грузы">
          <CargoDetailsModal
            cargo={cargoDetails ?? null}
            visible={openCargoId !== null}
            loading={cargoDetailsLoadingVisible}
            onClose={() => setOpenCargoId(null)}
            counterSnapshot={cargoModalCounterSnapshot}
            counterSnapshotLoading={countersLoading && openCargoId !== null}
            projectionFailed={projectionUiEnabled && projectionIndexFailed}
            historyRefreshKey={cargoModalHistoryKey}
          />
          <DataTable
            dataSource={cargo}
            pagination={false}
            size="small"
            rowKey="id"
            locale={{ emptyText: 'Грузы не добавлены' }}
            rowClassName={() => 'application-cargo-row-clickable'}
            onRow={(record) => ({
              onClick: () => {
                if (record.id != null && Number.isFinite(Number(record.id))) {
                  setOpenCargoId(Number(record.id));
                }
              },
            })}
            columns={[
              {
                title: 'Наименование',
                dataIndex: 'name',
                key: 'name',
                className: 'primary-cell',
                render: (name: string, record: ApplicationCargo) => (
                  <div>
                    <Text strong className="primary-cell">
                      {name || '—'}
                    </Text>
                    {record.description ? (
                      <div className="primary-cell-meta">{record.description}</div>
                    ) : null}
                  </div>
                ),
              },
            {
              title: 'Вес',
              dataIndex: 'totalWeightKg',
              key: 'weight',
              align: 'right' as const,
              render: (value: number | undefined) =>
                value != null && !Number.isNaN(Number(value)) ? `${Number(value).toLocaleString('ru-RU')} кг` : '—',
            },
            {
              title: 'Объём',
              dataIndex: 'volumeM3',
              key: 'volume',
              align: 'right' as const,
              render: (value: number | undefined) =>
                value != null && !Number.isNaN(Number(value)) ? `${Number(value).toFixed(2)} м³` : '—',
            },
            {
              title: 'Всего',
              dataIndex: 'quantity',
              key: 'quantity',
              align: 'right' as const,
              render: (value: number | undefined) =>
                value != null && !Number.isNaN(Number(value)) ? Number(value).toLocaleString('ru-RU') : '—',
            },
            {
              title: 'В маршрутах',
              key: 'inRoutes',
              align: 'right' as const,
              render: (_: unknown, record: ApplicationCargo) => {
                if (countersLoading) return <Spin size="small" />;
                return resolveCounters(record).inRoutes.toLocaleString('ru-RU');
              },
            },
            {
              title: 'В пути',
              key: 'inTransit',
              align: 'right' as const,
              render: (_: unknown, record: ApplicationCargo) => {
                if (countersLoading) return <Spin size="small" />;
                return resolveCounters(record).inTransit.toLocaleString('ru-RU');
              },
            },
            {
              title: 'Свободно',
              key: 'available',
              align: 'right' as const,
              render: (_: unknown, record: ApplicationCargo) => {
                if (countersLoading) return <Spin size="small" />;
                return resolveCounters(record).available.toLocaleString('ru-RU');
              },
            },
            {
              title: 'Доставлено',
              key: 'delivered',
              align: 'right' as const,
              render: (_: unknown, record: ApplicationCargo) => {
                if (countersLoading) return <Spin size="small" />;
                return resolveCounters(record).delivered.toLocaleString('ru-RU');
              },
            },
            {
              title: 'Перевозка',
              key: 'transport',
              render: (_: unknown, record: ApplicationCargo) => {
                if (countersLoading) return <Spin size="small" />;
                const snap = resolveCounters(record);
                return <Tag color={transportStatusTagColor(snap.transportLabel)}>{snap.transportLabel}</Tag>;
              },
            },
          ]}
        />
        {cargo.length > 0 ? (
          <Row
            style={{
              marginTop: 16,
              padding: '12px 16px',
              backgroundColor: '#fafafa',
              borderRadius: 6,
              border: '1px solid #f0f0f0',
            }}
          >
            <Col span={6}>
              <Text strong>Итого:</Text>
            </Col>
            <Col span={6}>
              <Text strong>{totals.weight.toLocaleString('ru-RU')} кг</Text>
            </Col>
            <Col span={6}>
              <Text strong>{totals.volume.toFixed(2)} м³</Text>
            </Col>
            <Col span={6}>
              <Text strong>{totals.places.toLocaleString('ru-RU')} мест</Text>
            </Col>
          </Row>
        ) : null}
        </Card>

        <Card size="small" title="История перемещений грузов">
          <DataTable<CargoHistoryRowWithName>
            rowKey={(row) => `${row.cargoId}-${row.id}`}
            dataSource={cargoTabHistoryRows}
            loading={cargoTabHistoryLoading}
            pagination={false}
            size="small"
            locale={{ emptyText: 'Нет истории' }}
            columns={[
              {
                title: 'Дата',
                key: 'dt',
                width: 110,
                render: (_: unknown, row: CargoHistoryRowWithName) => {
                  const stale = cargoHistoryRowIsStale(row);
                  return (
                    <Text type={stale ? 'secondary' : undefined}>
                      {dayjs(row.eventDate || row.createdAt).format('DD.MM.YYYY')}
                    </Text>
                  );
                },
              },
              {
                title: 'Груз',
                dataIndex: 'cargoName',
                key: 'cargoName',
                ellipsis: true,
                render: (t: string, row: CargoHistoryRowWithName) => (
                  <Text type={cargoHistoryRowIsStale(row) ? 'secondary' : undefined}>{t || '—'}</Text>
                ),
              },
              {
                title: 'Событие',
                key: 'ev',
                width: 200,
                ellipsis: true,
                render: (_: unknown, row: CargoHistoryRowWithName) => (
                  <Text type={cargoHistoryRowIsStale(row) ? 'secondary' : undefined}>
                    {cargoHistoryEventDisplayText(row)}
                  </Text>
                ),
              },
              {
                title: 'Описание',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
                render: (_: unknown, row: CargoHistoryRowWithName) => (
                  <Text type={cargoHistoryRowIsStale(row) ? 'secondary' : undefined}>
                    {cargoHistoryDescriptionDisplayText(row)}
                  </Text>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    );
  };

  const renderRoutesTab = () => (
    <Card size="small" title="Маршруты">
      <DataTable
        dataSource={application.routes || []}
        rowKey={(item: RouteApplication) => `${item.routeId}-${item.applicationId}`}
        size="small"
        pagination={false}
        locale={{ emptyText: 'Связанные маршруты отсутствуют' }}
        columns={[
          {
            title: '№ маршрута',
            key: 'number',
            render: (_: unknown, item: RouteApplication) => {
              const route = item.route;
              if (!route) return <Text type="secondary">—</Text>;
              const number = route.number?.trim() || `#${route.id}`;
              return (
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/routes/${route.id}`)}>
                  {number}
                </Button>
              );
            },
          },
          {
            title: 'Направление',
            key: 'direction',
            render: (_: unknown, item: RouteApplication) => getRouteDirection(item.route) || '—',
          },
          {
            title: 'Даты',
            key: 'dates',
            render: (_: unknown, item: RouteApplication) => {
              const route = item.route;
              if (!route) return '—';
              return `${formatDateShort(route.plannedLoadingDate)} - ${formatDateShort(route.plannedUnloadingDate)}`;
            },
          },
          {
            title: 'Статус',
            key: 'status',
            render: (_: unknown, item: RouteApplication) =>
              item.route ? <StatusCell status={String(item.route.status || '').toUpperCase()} /> : <Text type="secondary">—</Text>,
          },
          {
            title: 'Действия',
            key: 'actions',
            width: 140,
            render: (_: unknown, item: RouteApplication) =>
              item.route ? (
                <Button type="link" size="small" onClick={() => navigate(`/routes/${item.route.id}`)}>
                  Открыть
                </Button>
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
        ]}
      />
    </Card>
  );

  const renderPenaltiesTab = () => (
    <Card size="small" title="Условия и штрафы">
      <DataTable
        rowKey={(record: ApplicationCondition, index) => String(record.id ?? index)}
        size="small"
        pagination={false}
        dataSource={application.conditions || []}
        locale={{ emptyText: 'Условия и штрафы не добавлены' }}
        columns={[
          {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            render: (type: ApplicationCondition['type']) => APPLICATION_CONDITION_LABELS[type] || type,
          },
          {
            title: 'Условия',
            key: 'terms',
            render: (_: unknown, record: ApplicationCondition) => formatApplicationCondition(record).terms,
          },
          {
            title: 'Размер',
            key: 'amount',
            render: (_: unknown, record: ApplicationCondition) => formatApplicationCondition(record).amount,
          },
          {
            title: 'Комментарий',
            dataIndex: 'comment',
            key: 'comment',
            render: (comment?: string | null) => comment || <Text type="secondary">—</Text>,
          },
        ]}
      />
    </Card>
  );

  return (
    <>
      {renderActions()}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'main', label: 'Основная информация', children: renderMainTab() },
          { key: 'tasks', label: 'Задачи и контроль', children: renderTasksTab() },
          { key: 'finance', label: 'Финансы', children: renderFinanceTab() },
          { key: 'cargo', label: 'Грузы', children: application.cargo?.length ? renderCargoTab() : <Empty description="Грузы не добавлены" /> },
          { key: 'routes', label: 'Маршруты', children: renderRoutesTab() },
          { key: 'documents', label: 'Документы', children: <ApplicationDocumentsTab application={application} /> },
          { key: 'penalties', label: 'Штрафы', children: renderPenaltiesTab() },
        ]}
      />

      <ApplicationEventsModal
        visible={isEventsModalVisible}
        applicationId={application.id}
        onClose={() => setIsEventsModalVisible(false)}
      />

      <CreatePaymentModal
        visible={clientPayModalVisible}
        routeId={null}
        routeApplicationId={application.id}
        initialObligationId={clientPayObligationId}
        preloadedObligations={clientIncomeObligationsForModal.length > 0 ? clientIncomeObligationsForModal : null}
        obligationDirectionFilter="INCOME"
        onClose={() => {
          setClientPayModalVisible(false);
          setClientPayObligationId(null);
        }}
        onSuccess={async () => {
          setClientPayModalVisible(false);
          setClientPayObligationId(null);
          await onRefresh?.();
        }}
      />

      <CreateClientInvoiceModal
        visible={isCreateInvoiceModalVisible}
        onCancel={() => setIsCreateInvoiceModalVisible(false)}
        onSuccess={async () => {
          setIsCreateInvoiceModalVisible(false);
          dispatch(
            workspaceApi.util.invalidateTags(['Workspace', { type: 'ApplicationControlTasks', id: application.id }])
          );
          await refetchApplicationControlTasks();
          await onRefresh?.();
        }}
        preselectedApplicationId={application.id}
      />
    </>
  );
};

export default ApplicationDetails;
